import { useEffect } from 'react';
import { attachPhaseTabSync } from 'collections/phase';
import { attachPhaseProgressReconciler, attachPlanBoardTabSync } from 'collections/plan-board';
import { attachProjectTabSync } from 'collections/project';
import { attachRolesTabSync } from 'collections/roles/tab-sync';
import { attachStatusUpdatesTabSync } from 'collections/status-updates/tab-sync';
import { closeTabSync, setTabSyncIdentity } from 'collections/tab-sync';
import { attachTaskTabSync } from 'collections/tasks/tab-sync';
import { attachTeamsTabSync } from 'collections/teams/tab-sync';
import { useAccountDataQuery } from 'queries/account';
/**
 * Mount cross-tab state-db sync for the session. Call ONCE, at the app root.
 *
 * Identity is published before any attach so the very first outbound envelope
 * already carries the right `accountId`/`userId` — a message sent with null
 * identity would be dropped by every peer.
 *
 * No longer behind a prerelease flag. The remaining condition is a correctness
 * guard, not a rollout one: until the session's principal is known, an outbound
 * envelope would be dropped by every peer anyway, and accepting inbound data
 * before we know who we are would defeat the principal check that isolates
 * accounts.
 *
 * Identity comes from `useAccountDataQuery` — the query `useInitData` waits on —
 * and NOT from the `useAccountSafe`/`useUserSafe` contexts. Those return null
 * until their providers hydrate, which races React's effect ordering and left
 * identity permanently null: `attachTabSync` was never called and cross-tab sync
 * silently never activated. Reading the query makes the ids non-null exactly
 * when the init fetch resolves.
 *
 * `UserProfileInfo` declares a top-level `userId` that the API does not
 * populate — the real id lives on the nested `user`.
 */ export const useStateDbTabSync = ()=>{
    var _ref, _ref1;
    var _initData_account, _initData_userProfile_user, _initData_userProfile;
    const { data: initData } = useAccountDataQuery();
    const accountId = (_ref = initData === null || initData === void 0 ? void 0 : (_initData_account = initData.account) === null || _initData_account === void 0 ? void 0 : _initData_account.accountId) !== null && _ref !== void 0 ? _ref : null;
    const userId = (_ref1 = initData === null || initData === void 0 ? void 0 : (_initData_userProfile = initData.userProfile) === null || _initData_userProfile === void 0 ? void 0 : (_initData_userProfile_user = _initData_userProfile.user) === null || _initData_userProfile_user === void 0 ? void 0 : _initData_userProfile_user.userId) !== null && _ref1 !== void 0 ? _ref1 : null;
    useEffect(()=>{
        setTabSyncIdentity({
            accountId,
            userId
        });
    }, [
        accountId,
        userId
    ]);
    // Identity-independent, unlike the cross-tab attaches below: this keeps
    // phase `progressStatus`/`totalTaskCount` coherent with task writes from
    // ANY surface (task list, kanban, subtask add, bulk edit, agents...), not
    // just the paginated plan board. Previously only attached lazily from
    // `fetchPlanBoard`/`ingestLanePage`, so a task created before the paginated
    // board had ever loaded in this session (e.g. from the task list, or a
    // project still on the legacy non-paginated swimlane) left the project
    // overview's "All phases" card's progress bar permanently unreconciled —
    // no code was listening yet. Attaching it here, once, at app root, closes
    // that gap; the lazy calls stay as a harmless, idempotent safety net.
    useEffect(()=>{
        attachPhaseProgressReconciler();
    }, []);
    useEffect(()=>{
        // Wait for a real session: envelopes sent while unidentified are dropped on
        // receipt, and accepting peer data before we know who we are would defeat
        // the principal guard.
        if (accountId == null || userId == null) {
            return undefined;
        }
        setTabSyncIdentity({
            accountId,
            userId
        });
        const detachers = [
            attachTaskTabSync(),
            attachProjectTabSync(),
            attachPhaseTabSync(),
            attachPlanBoardTabSync(),
            attachTeamsTabSync(),
            attachRolesTabSync(),
            attachStatusUpdatesTabSync()
        ];
        return ()=>{
            detachers.forEach((detach)=>detach());
            // Drop the channel on logout / account switch so a stale peer cannot keep
            // talking to the previous principal's tab.
            closeTabSync();
        };
    }, [
        accountId,
        userId
    ]);
};
